import { watch, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRafFn } from '@vueuse/core';
import OLMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import { useMapStyleStore } from 'shared/mapStyles/stores/mapStyleStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useShapeStore } from 'shared/shapes/stores/shapeStore';
import { usePackageStore } from 'shared/packages/stores/packageStore';
import { StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { StorageAreaStatus } from 'shared/storageAreas/domain/storageAreaStatus';
import type { MapStyle } from 'shared/mapStyles/domain/mapStyle';
import type { PlaceStyle } from 'shared/mapStyles/domain/placeStyle';
import type { PlaceContext } from 'shared/mapStyles/domain/placeContext';
import type { StoragePlace } from 'shared/storageAreas/domain/storageArea';
import { computePlaceGeometry } from '../domain/placeGeometry';
import { padExtent, ringExtent, extentsIntersect, type Extent } from '../domain/viewportCulling';
import { placeStyle } from '../styles/placeStyle';

// Extra viewport margin (fraction of view size) kept rendered on each side so
// features just outside the view don't pop in during a pan.
const VIEWPORT_PADDING = 0.25;

export interface PlacesLayerOptions {
  mapStyle?: Ref<MapStyle>;
  selectedPlaceIds?: Ref<string[]>;
  /** Id of the storage area currently being edited, if any. */
  editingAreaId?: Ref<string | undefined>;
  /** Working copy of the edited area's places, rendered live while editing. */
  editingPlaces?: Ref<StoragePlace[]>;
  /**
   * Context for an area being created or copied that is not yet saved in the
   * store. When set together with editingPlaces and editingShapeFeature, its
   * places are rendered live against the shape so they are visible (and follow
   * the shape) before the area exists in the store.
   */
  editingArea?: Ref<{ id: string; name: string; status: StorageAreaStatus } | undefined>;
  /**
   * Live geometry of the edited storage area's shape. When set (while editing),
   * places are laid out against this instead of the saved shape so they follow
   * the area immediately as it is moved or rotated.
   */
  editingShapeFeature?: Ref<GeoJSON.Feature | undefined>;
}

export function usePlacesLayer(options?: PlacesLayerOptions) {
  const mapStyleStore = useMapStyleStore();
  const storageAreaStore = useStorageAreaStore();
  const shapeStore = useShapeStore();
  const packageStore = usePackageStore();

  const source = new VectorSource();
  const selectedStyle = getMapStyle();

  let olMap: OLMap | null = null;
  let renderScheduled = true;

  shapeStore.changed.on(() => scheduleRender());
  packageStore.changed.on(() => scheduleRender());
  storageAreaStore.changed.on(() => scheduleRender());
  watch(selectedStyle, () => scheduleRender());
  if (options?.selectedPlaceIds) {
    watch(options.selectedPlaceIds, () => scheduleRender());
  }
  if (options?.editingPlaces) {
    watch(options.editingPlaces, () => scheduleRender(), { deep: true });
  }
  if (options?.editingAreaId) {
    watch(options.editingAreaId, () => scheduleRender());
  }
  if (options?.editingArea) {
    watch(options.editingArea, () => scheduleRender(), { deep: true });
  }
  if (options?.editingShapeFeature) {
    watch(options.editingShapeFeature, () => scheduleRender());
  }

  useRafFn(() => {
    if (!renderScheduled) return;
    renderScheduled = false;
    rebuild();
  });

  return {
    attach,
  };

  function getMapStyle() {
    if (options && options.mapStyle) return options.mapStyle;
    const { selectedStyle } = storeToRefs(mapStyleStore);
    return selectedStyle;
  }

  function scheduleRender() {
    renderScheduled = true;
  }

  function attach(map: OLMap) {
    olMap = map;
    const layer = new VectorLayer({
      source,
      style: placeStyle,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
    layer.setZIndex(10);
    map.addLayer(layer);

    // Re-cull to the new viewport after every pan/zoom.
    map.on('moveend', () => scheduleRender());

    rebuild();
    return source;
  }

  function getPaddedExtent(): Extent | null {
    if (!olMap) return null;
    const size = olMap.getSize();
    if (!size) return null;
    const extent = olMap.getView().calculateExtent(size) as Extent;
    return padExtent(extent, VIEWPORT_PADDING);
  }

  function computeOccupancy(): Map<string, number> {
    const occupancy = new Map<string, number>();
    for (const pkg of packageStore.packages) {
      for (const placeId of pkg.occupiedStoragePlaceIds) {
        occupancy.set(placeId, (occupancy.get(placeId) ?? 0) + 1);
      }
    }
    return occupancy;
  }

  function rebuild() {
    source.clear();

    const placeStyleFns = selectedStyle.value?.place;
    if (!placeStyleFns) return;

    const paddedExtent = getPaddedExtent();
    const occupancy = computeOccupancy();

    const editingAreaId = options?.editingAreaId?.value;
    let editingAreaRendered = false;

    for (const storageArea of storageAreaStore.storageAreas) {
      // Render places from PackageStorage (type 13), PackageOutput (type 15), or PackageIntake (type 16)
      let packageData = storageArea.packageStorageData;
      if (!packageData) {
        packageData = storageArea.packagePlaceData;
      }
      if (!packageData) continue;

      const isEditing = editingAreaId === storageArea.id;
      const placesToRender =
        isEditing && options?.editingPlaces ? options.editingPlaces.value : packageData.places;
      if (placesToRender.length === 0) continue;

      // While editing, follow the live (in-edit) area geometry so places move
      // and rotate together with the storage area before it is saved.
      const liveShapeFeature = isEditing ? options?.editingShapeFeature?.value : undefined;

      let shapeFeature = liveShapeFeature;
      if (!shapeFeature) {
        const shapes = shapeStore.getByStorageAreaId(storageArea.id);
        if (shapes.length === 0) continue;
        shapeFeature = shapes[0]!.shapeGeoJson ?? undefined;
      }
      if (!shapeFeature) continue;

      // Skip whole areas outside the viewport before computing place geometry.
      // The area being edited is always rendered so live edits stay visible.
      if (paddedExtent && !isEditing && !areaInExtent(shapeFeature, paddedExtent)) continue;

      if (isEditing) editingAreaRendered = true;

      renderArea(
        { id: storageArea.id, name: storageArea.name, status: storageArea.status },
        placesToRender,
        shapeFeature,
        placeStyleFns,
        occupancy,
      );
    }

    // Render an area being created or copied that is not yet in the store, so
    // its copied places are visible and follow the live shape before it is saved.
    const editingArea = options?.editingArea?.value;
    const editingShapeFeature = options?.editingShapeFeature?.value;
    const editingPlaces = options?.editingPlaces?.value;
    if (
      !editingAreaRendered &&
      editingArea &&
      editingShapeFeature &&
      editingPlaces &&
      editingPlaces.length > 0
    ) {
      renderArea(editingArea, editingPlaces, editingShapeFeature, placeStyleFns, occupancy);
    }
  }

  function renderArea(
    area: { id: string; name: string; status: StorageAreaStatus },
    placesToRender: StoragePlace[],
    shapeFeature: GeoJSON.Feature,
    placeStyleFns: PlaceStyle,
    occupancy: Map<string, number>,
  ) {
    // Render lower z-indices first so higher levels appear on top.
    const orderedPlaces = [...placesToRender].sort((a, b) => a.zIndex - b.zIndex);
    const geometries = computePlaceGeometry(shapeFeature, orderedPlaces);

    for (const { place, coordinates } of geometries) {
      const packageCount = occupancy.get(place.id) ?? 0;

      const context: PlaceContext = {
        index: place.index,
        disabled: place.disabled,
        occupied: packageCount > 0,
        packageCount,
        maxStackCount: place.maxStackCount,
        maxWeight: place.maxWeight,
        maxHeight: place.maxHeight,
        x: place.x,
        y: place.y,
        length: place.length,
        width: place.width,
        zIndex: place.zIndex,
        accessDirections: place.accessDirections,
        accessFront: (place.accessDirections & 1) === 1,
        accessRight: (place.accessDirections & 2) === 2,
        accessBack: (place.accessDirections & 4) === 4,
        accessLeft: (place.accessDirections & 8) === 8,
        storageArea: {
          id: area.id,
          name: area.name,
          empty: area.status === StorageAreaStatus.Empty,
        },
        column: place.column,
        row: place.row,
      };

      if (!placeStyleFns.visible(context)) continue;

      const selected = options?.selectedPlaceIds?.value.includes(place.id) ?? false;

      const baseOutlineColor = placeStyleFns.outlineColor(context);
      const accessibleOutlineColor =
        placeStyleFns.accessibleOutlineColor(context) ?? baseOutlineColor;
      const inaccessibleOutlineColor =
        placeStyleFns.inaccessibleOutlineColor(context) ?? baseOutlineColor;

      const feature = new Feature({
        geometry: new Polygon([coordinates]),
      });

      feature.setId(place.id);
      feature.setProperties({
        placeId: place.id,
        storageAreaId: area.id,
        label: placeStyleFns.label(context),
        outlineColor: selected ? '#2563eb' : baseOutlineColor,
        outlineSize: selected
          ? Math.max(3, placeStyleFns.outlineSize(context))
          : placeStyleFns.outlineSize(context),
        accessibleOutlineColor: selected ? '#2563eb' : accessibleOutlineColor,
        inaccessibleOutlineColor: selected ? '#2563eb' : inaccessibleOutlineColor,
        accessFront: context.accessFront,
        accessRight: context.accessRight,
        accessBack: context.accessBack,
        accessLeft: context.accessLeft,
        textAlignment: placeStyleFns.textAlignment(context),
        textPosition: placeStyleFns.textPosition(context),
        textOpacity: placeStyleFns.textOpacity(context),
        textColor: placeStyleFns.textColor(context),
        textSize: placeStyleFns.textSize(context),
        backgroundColor: placeStyleFns.backgroundColor(context),
        backgroundPattern: placeStyleFns.backgroundPattern(context),
        backgroundOpacity: placeStyleFns.backgroundOpacity(context),
      });

      source.addFeature(feature);
    }
  }

  function areaInExtent(shapeFeature: GeoJSON.Feature, extent: Extent): boolean {
    const geometry = shapeFeature.geometry;
    if (!geometry || geometry.type !== 'Polygon') return true;
    const ring = geometry.coordinates[0];
    if (!ring) return true;
    const areaExtent = ringExtent(ring);
    if (!areaExtent) return true;
    return extentsIntersect(areaExtent, extent);
  }
}
